'use client';

import React, { useState } from 'react';
import { Card } from '@/components/common/Card';
import { AppImage } from '@/components/common/AppImage';
import { Users, Wallet, User, Trophy, Calendar, Shield } from 'lucide-react';
import type { Player, Tournament } from '@/lib/types/auction';
import { formatCurrency } from '@/lib/utils/format';
import { PlayerCard } from '@/components/player/PlayerCard';
import { Tabs } from '@/components/common/Tabs';
import { motion } from 'framer-motion';
import { ShareButton } from '@/components/common/ShareButton';
import { TeamActions } from '@/components/team/TeamActions';

export interface TeamDetailsProps {
  name: string;
  logo?: string;
  owner?: string;
  budget: number;
  players: Player[];
  tournament?: Tournament;
  spent?: number;
  maxPlayers?: number;
  onAction?: () => void;
}

export function TeamDetails({
  name,
  logo,
  owner,
  budget,
  players,
  tournament,
  spent = 0,
  maxPlayers = 0,
  onAction,
}: TeamDetailsProps) {
  const [activeTab, setActiveTab] = useState(0);
  const remainingBudget = budget - spent;
  const playerCount = players.length;
  const remainingPlayers = maxPlayers > 0 ? maxPlayers - playerCount : 0;

  const renderSquad = () => (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
      {players.map((player) => (
        <PlayerCard key={player.id} player={player} tournament={tournament} />
      ))}
      {players.length === 0 && (
        <div className="col-span-full py-8 text-center text-gray-500">
          No players in squad yet
        </div>
      )}
    </div>
  );

  const renderStats = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          icon={<Wallet className="h-5 w-5 text-accent-green" />}
          label="Budget"
          value={formatCurrency(budget)}
          bgColor="from-accent-green/5 to-accent-green/10"
        />
        <StatCard
          icon={<Wallet className="h-5 w-5 text-accent-blue" />}
          label="Spent"
          value={formatCurrency(spent)}
          bgColor="from-accent-blue/5 to-accent-blue/10"
        />
        <StatCard
          icon={<Users className="h-5 w-5 text-primary-600" />}
          label="Players"
          value={`${playerCount} / ${maxPlayers || '∞'}`}
          bgColor="from-primary-50/80 to-primary-100/80"
        />
      </div>

      <div className="rounded-lg bg-gradient-to-r from-primary-50 to-primary-100/50 p-4 shadow-sm">
        <h3 className="mb-3 text-lg font-semibold text-gray-900">
          Budget Breakdown
        </h3>
        <div className="mb-2 flex items-center justify-between">
          <span className="text-sm text-gray-600">Total Budget</span>
          <span className="font-medium text-gray-900">
            {formatCurrency(budget)}
          </span>
        </div>
        <div className="mb-2 flex items-center justify-between">
          <span className="text-sm text-gray-600">Spent</span>
          <span className="font-medium text-accent-blue">
            {formatCurrency(spent)}
          </span>
        </div>
        <div className="mb-3 flex items-center justify-between">
          <span className="text-sm text-gray-600">Remaining</span>
          <span className="font-medium text-accent-green">
            {formatCurrency(remainingBudget)}
          </span>
        </div>

        {/* Budget Progress Bar */}
        <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
          <div
            className="h-full rounded-full bg-gradient-to-r from-primary-500 to-accent-blue"
            style={{ width: `${Math.min(100, (spent / budget) * 100)}%` }}
          />
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Team Header Card */}
      <Card allowOverflow className="p-0 md:p-0">
        <div className="relative rounded-lg bg-gradient-to-r from-primary-500 to-accent-blue p-2 text-white sm:p-4">
          <div className="flex flex-col items-center space-y-4 sm:flex-row sm:space-x-6 sm:space-y-0">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="relative h-24 w-24 overflow-hidden rounded-full border-4 border-white/70 shadow-lg sm:h-32 sm:w-32"
            >
              <AppImage
                src={logo || ''}
                alt={`${name} logo`}
                shape="circle"
                gradient
                fallbackText={name}
                size="md"
                className="h-full w-full object-cover"
              />
            </motion.div>

            <div className="text-center sm:text-left">
              <div className="flex items-center justify-center space-x-2 sm:justify-start">
                <h1 className="text-2xl font-bold sm:text-3xl">{name}</h1>
              </div>

              {owner && (
                <div className="mt-2 flex items-center justify-center space-x-2 text-white/90 sm:justify-start">
                  <User className="h-4 w-4" />
                  <span>Owned by {owner}</span>
                </div>
              )}

              {tournament && (
                <div className="mt-2 flex items-center justify-center space-x-2 text-white/90 sm:justify-start">
                  <Trophy className="h-4 w-4" />
                  <span>Participating in {tournament.name}</span>
                </div>
              )}
            </div>

            {/* Actions Section */}
            <div className="absolute right-4 top-2 flex items-center gap-2 sm:top-4">
              <ShareButton
                title={`${name} - Team Profile`}
                description={`Check out ${name}'s team profile on Auction Chase${tournament ? ` from ${tournament.name}` : ''}`}
                url={`${window.location.origin}/team/${players[0]?.team?.id || ''}`}
                hashtags={['AuctionChase', 'Team']}
                variant="icon"
                theme="dark"
                image={logo || `${window.location.origin}/images/og.jpg`}
              />
              {players[0]?.team && (
                <TeamActions
                  team={players[0].team}
                  tournament={tournament}
                  onAction={onAction}
                />
              )}
            </div>
          </div>

          {/* Quick Stats */}
          <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
            <QuickStat
              icon={<Wallet />}
              label="Budget"
              value={formatCurrency(budget)}
            />
            <QuickStat
              icon={<Users />}
              label="Players"
              value={playerCount.toString()}
            />
            <QuickStat
              icon={<Shield />}
              label="Remaining"
              value={formatCurrency(remainingBudget)}
            />
            <QuickStat
              icon={<Calendar />}
              label="Slots Left"
              value={`${remainingPlayers}`}
            />
          </div>
        </div>
      </Card>

      {/* Tabs Section */}
      <Card>
        <Tabs
          items={[
            { label: 'Squad', content: renderSquad() },
            { label: 'Team Stats', content: renderStats() },
          ]}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />
      </Card>
    </div>
  );
}

// Helper Components
interface QuickStatProps {
  icon: React.ReactNode;
  label: string;
  value: string;
}

function QuickStat({ icon, label, value }: QuickStatProps) {
  return (
    <div className="rounded-lg bg-white/10 p-3 text-center">
      <div className="mb-1 flex justify-center">{icon}</div>
      <p className="text-xs font-medium text-white/80">{label}</p>
      <p className="text-lg font-bold">{value}</p>
    </div>
  );
}

interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  bgColor: string;
}

function StatCard({ icon, label, value, bgColor }: StatCardProps) {
  return (
    <div
      className={`flex items-center justify-between rounded-lg bg-gradient-to-r ${bgColor} p-4`}
    >
      <div className="flex items-center gap-3">
        {icon}
        <span className="text-sm text-gray-700">{label}</span>
      </div>
      <span className="text-base font-semibold text-gray-900">{value}</span>
    </div>
  );
}
